import { useState, useEffect, useRef, useCallback } from 'react';
import type { RefObject, MouseEvent as ReactMouseEvent } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Slider,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { Printer, X } from 'lucide-react';
import { computeFitScale, computePageOffsets } from './print-fit';

const MM_TO_PX = 3.7795275591;
const PAGE_MARGIN_MM = 10;

export function buildPrintHtml(args: {
  styles: string;
  content: string;
  paperWmm: number;
  paperHmm: number;
  fitScale: number;
}): string {
  const { styles, content, paperWmm, paperHmm, fitScale } = args;
  const innerWmm = paperWmm - 2 * PAGE_MARGIN_MM;
  const innerHmm = paperHmm - 2 * PAGE_MARGIN_MM;
  // The iframe document sizes body to the printable AREA (paper minus margins).
  // The outer React Box draws the white "margin" around the iframe visually.
  // For real printing, @page { margin } applies the printer margin separately —
  // body width matches the printable area exactly, so layout is identical.
  // CSS `zoom` is the ONLY scaling mechanism. It shrinks the layout box, so
  // scrollHeight reflects the scaled content. Do not add a CSS transform or an
  // inverse width next to it: Chromium and Firefox >= 126 both support `zoom`,
  // and the two would compound (0.8 would draw at 0.64 while scrollHeight
  // reports 0.8), breaking the page-break math.
  const zoomOpen =
    fitScale < 1
      ? `<div class="__fit" style="zoom:${fitScale};">`
      : '';
  const zoomClose = fitScale < 1 ? '</div>' : '';
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #fff; overflow: hidden; }
  @page { size: ${paperWmm}mm ${paperHmm}mm; margin: ${PAGE_MARGIN_MM}mm; }
  body { margin: 0; padding: 0; width: ${innerWmm}mm; min-height: ${innerHmm}mm; }
  ${styles}
  /* Modal owns all page margins. Strip internal padding/positioning from
     well-known print-view roots so preview and print render identically. */
  .gc-print, .cr-print, .pv-root { padding: 0 !important; }
  @media print {
    .gc-print, .cr-print, .pv-root {
      position: static !important;
      width: 100% !important;
      padding: 0 !important;
      left: auto !important;
      top: auto !important;
    }
  }
</style></head><body>${zoomOpen}${content}${zoomClose}
<script>
  // Forward Ctrl/Cmd+wheel up to the parent modal so it can update zoom.
  // Iframes don't bubble events to the parent document — we relay via postMessage.
  window.addEventListener('wheel', function(e){
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    parent.postMessage({ type: 'pp-zoom', deltaY: e.deltaY }, '*');
  }, { passive: false });
<\/script>
</body></html>`;
}

export interface PrintPreviewModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  printRef: RefObject<HTMLDivElement | null>;
  orientation?: 'portrait' | 'landscape';
  allowOrientationToggle?: boolean;
  /** Modal width — defaults to 95vw for wide reports; callers with narrow content can pass e.g. "80vw". */
  width?: string;
  /**
   * Shrink so each `.page-section` (or the whole body when there is none) fits one page,
   * down to a readable floor; taller content paginates as before.
   */
  fitToPage?: boolean;
}

export function PrintPreviewModal({
  opened,
  onClose,
  title,
  printRef,
  orientation: initialOrientation = 'landscape',
  allowOrientationToggle = false,
  width = '95vw',
  fitToPage = false,
}: PrintPreviewModalProps) {
  const measurementRef = useRef<HTMLIFrameElement>(null);
  const pageIframeRefs = useRef<(HTMLIFrameElement | null)[]>([]);
  const panViewportRef = useRef<HTMLDivElement>(null);
  const panStateRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
  }>({
    active: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(initialOrientation);
  const [zoom, setZoom] = useState(90);
  const [isDragging, setIsDragging] = useState(false);
  const [pageStartOffsetsMm, setPageStartOffsetsMm] = useState<number[]>([0]);
  const [totalHeightMm, setTotalHeightMm] = useState<number>(297);
  const [fitScale, setFitScale] = useState<number>(1);

  // Paper dimensions (A4)
  const paperWmm = orientation === 'landscape' ? 297 : 210;
  const paperHmm = orientation === 'landscape' ? 210 : 297;
  const innerWmm = paperWmm - 2 * PAGE_MARGIN_MM;
  const innerHmm = paperHmm - 2 * PAGE_MARGIN_MM;
  const paperW = `${paperWmm}mm`;
  const paperH = `${paperHmm}mm`;
  const innerW = `${innerWmm}mm`;
  const innerH = `${innerHmm}mm`;
  const orientationLabel = orientation === 'landscape' ? 'Landscape' : 'Portrait';
  const pageCount = pageStartOffsetsMm.length;

  // Reset orientation to prop value whenever modal opens
  useEffect(() => {
    if (opened) setOrientation(initialOrientation);
  }, [opened, initialOrientation]);

  const getPrintParts = useCallback(() => {
    if (!printRef.current) return null;
    const styleTag = printRef.current.querySelector('style');
    return {
      styles: styleTag?.innerHTML ?? '',
      content: printRef.current.innerHTML,
    };
  }, [printRef]);

  // Each page iframe writes the SAME full document. The outer Box clips and
  // translates to reveal only the slice that belongs to this page.
  const writePageFrame = useCallback(
    (frame: HTMLIFrameElement) => {
      const parts = getPrintParts();
      if (!parts) return;
      const doc = frame.contentDocument;
      if (!doc) return;
      doc.open();
      doc.write(
        buildPrintHtml({
          styles: parts.styles,
          content: parts.content,
          paperWmm,
          paperHmm,
          fitScale,
        }),
      );
      doc.close();
    },
    [getPrintParts, paperWmm, paperHmm, fitScale],
  );

  // Measure full content + compute fit-to-width scale + snap page boundaries
  // to row/section tops so seams don't cut through table rows.
  const measureAndUpdate = useCallback(() => {
    const m = measurementRef.current;
    const parts = getPrintParts();
    if (!m || !parts) return;
    const doc = m.contentDocument;
    if (!doc) return;

    // Pass 1: render unscaled to detect horizontal overflow
    doc.open();
    doc.write(
      buildPrintHtml({
        styles: parts.styles,
        content: parts.content,
        paperWmm,
        paperHmm,
        fitScale: 1,
      }),
    );
    doc.close();

    setTimeout(() => {
      const body = doc.body;
      if (!body) return;

      // body width is the printable area (paper - margins). When content
      // overflows that, body.scrollWidth grows past body.clientWidth.
      const naturalWidth = body.scrollWidth;
      const innerWidthPx = innerWmm * MM_TO_PX;
      const usableHeightPx = innerHmm * MM_TO_PX;
      let sectionHeightsPx: number[] = [];
      if (fitToPage) {
        // Each section's page "slot": from the previous slot's end (page top for
        // the first) to the next section's top (own bottom for the last). This
        // counts spacing around sections, which a bare section height misses.
        const rects = Array.from(doc.querySelectorAll('.page-section')).map((el) =>
          (el as HTMLElement).getBoundingClientRect(),
        );
        sectionHeightsPx = rects.map((r, i) => {
          const start = i === 0 ? 0 : r.top;
          const end = i + 1 < rects.length ? rects[i + 1].top : r.bottom;
          return end - start;
        });
        if (sectionHeightsPx.length === 0) sectionHeightsPx = [body.scrollHeight];
      }
      const newFitScale = computeFitScale({
        naturalWidthPx: naturalWidth,
        innerWidthPx,
        sectionHeightsPx,
        usableHeightPx,
        fitToPage,
      });

      // Pass 2: re-render scaled if needed
      if (newFitScale < 1) {
        doc.open();
        doc.write(
          buildPrintHtml({
            styles: parts.styles,
            content: parts.content,
            paperWmm,
            paperHmm,
            fitScale: newFitScale,
          }),
        );
        doc.close();
      }

      setTimeout(() => {
        // body.scrollHeight can exceed the real content by a few px once `zoom`
        // is applied (min-height + rounding), which would spill a near-empty
        // second page. When fitting, measure to the bottom of the last section.
        let totalPx = doc.body.scrollHeight;
        if (fitToPage) {
          const bottoms = Array.from(doc.querySelectorAll('.page-section')).map(
            (el) => (el as HTMLElement).getBoundingClientRect().bottom,
          );
          if (bottoms.length > 0) totalPx = Math.min(totalPx, Math.max(...bottoms));
        }
        const totalMm = totalPx / MM_TO_PX;
        const usablePx = innerHmm * MM_TO_PX;

        // Collect break candidates: tops of every row/section element.
        const candidates: number[] = [0];
        const sectionTops: number[] = [];
        doc.querySelectorAll('tr, tbody, thead, .page-section').forEach((el) => {
          const rect = (el as HTMLElement).getBoundingClientRect();
          // body's getBoundingClientRect().top is 0 in this isolated doc
          candidates.push(rect.top);
          if (el.classList.contains('page-section')) sectionTops.push(rect.top);
        });

        const offsets = computePageOffsets({
          candidatesPx: candidates,
          sectionTopsPx: sectionTops,
          totalPx,
          usablePx,
          preferSectionBreaks: fitToPage,
        });
        setPageStartOffsetsMm(offsets.map((px) => px / MM_TO_PX));
        setTotalHeightMm(Math.max(totalMm, innerHmm));
        setFitScale(newFitScale);
      }, 80);
    }, 80);
  }, [getPrintParts, paperWmm, paperHmm, innerWmm, innerHmm, fitToPage]);

  // Re-measure on open / orientation change
  useEffect(() => {
    if (!opened) return;
    const t = setTimeout(measureAndUpdate, 60);
    return () => clearTimeout(t);
  }, [opened, orientation, measureAndUpdate]);

  // Re-render every visible page iframe whenever measurement updates.
  // Frames are also written synchronously on ref-assign (see iframe ref below),
  // so a freshly-mounted iframe never depends on this effect's timing.
  useEffect(() => {
    if (!opened) return;
    pageIframeRefs.current.forEach((f) => {
      if (f) writePageFrame(f);
    });
  }, [opened, orientation, pageStartOffsetsMm, fitScale, writePageFrame]);

  // Center the preview horizontally whenever zoom/pageCount/orientation change
  useEffect(() => {
    if (!opened) return;
    const v = panViewportRef.current;
    if (!v) return;
    const t = setTimeout(() => {
      v.scrollLeft = Math.max(0, (v.scrollWidth - v.clientWidth) / 2);
    }, 80);
    return () => clearTimeout(t);
  }, [opened, orientation, zoom, pageCount]);

  function handleSendToPrint() {
    const parts = getPrintParts();
    if (!parts) return;
    const iframe = document.createElement('iframe');
    iframe.style.cssText =
      'position: fixed; width: 0; height: 0; border: none; left: -9999px;';
    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    };
    // Using srcdoc means `load` fires once for the real content, not for
    // an intermediate about:blank document.
    iframe.srcdoc = buildPrintHtml({
      styles: parts.styles,
      content: parts.content,
      paperWmm,
      paperHmm,
      fitScale,
    });
    iframe.onload = triggerPrint;
    document.body.appendChild(iframe);
    // Fallback in case `load` is suppressed.
    setTimeout(triggerPrint, 500);
    onClose();
  }

  // Drag-to-pan
  const handlePanStart = (e: ReactMouseEvent<HTMLDivElement>) => {
    const v = panViewportRef.current;
    if (!v) return;
    panStateRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: v.scrollLeft,
      scrollTop: v.scrollTop,
    };
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: globalThis.MouseEvent) => {
      const v = panViewportRef.current;
      const s = panStateRef.current;
      if (!v || !s.active) return;
      v.scrollLeft = s.scrollLeft - (e.clientX - s.startX);
      v.scrollTop = s.scrollTop - (e.clientY - s.startY);
    };
    const onUp = () => {
      panStateRef.current.active = false;
      setIsDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // Ctrl/Cmd + wheel → zoom in/out. Wheel events have two reachable sources:
  //   1. Native (parent doc) — over gutters, the paper sheet wrapper, or over
  //      the iframe pixels (because `pointer-events: none` on the iframe
  //      redirects them to the Box behind). Caught on `window` in the capture
  //      phase so no ancestor can swallow them; preventDefault'd to stop the
  //      browser's whole-page zoom. Scoped by checking the target is inside
  //      our pannable viewport.
  //   2. postMessage — fallback for browsers that don't redirect wheel via
  //      pointer-events: none. The iframe's inline script (see buildPrintHtml)
  //      relays Ctrl+wheel up to us.
  useEffect(() => {
    if (!opened) return;
    const applyDelta = (deltaY: number) => {
      const step = deltaY > 0 ? -5 : 5;
      setZoom((z) => Math.min(150, Math.max(15, z + step)));
    };
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const v = panViewportRef.current;
      if (!v) return;
      const target = e.target as Node | null;
      if (target && !v.contains(target)) return;
      e.preventDefault();
      applyDelta(e.deltaY);
    };
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (d && typeof d === 'object' && d.type === 'pp-zoom' && typeof d.deltaY === 'number') {
        applyDelta(d.deltaY);
      }
    };
    // capture-phase + passive:false so we always fire first and can block
    // the browser's default page-zoom action.
    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('wheel', onWheel, { capture: true } as EventListenerOptions);
      window.removeEventListener('message', onMessage);
    };
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      title={undefined}
      size={width}
      padding={0}
      styles={{
        content: {
          borderRadius: 12,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width,
          maxWidth: width,
          height: '95vh',
        },
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
        },
        inner: { padding: '24px 16px' },
      }}
    >
      {/* Off-screen measurement iframe (used to compute page count + fit scale) */}
      <iframe
        ref={measurementRef}
        title="Print Preview Measurement"
        aria-hidden
        style={{
          position: 'absolute',
          left: -99999,
          top: 0,
          width: innerW,
          height: 1,
          border: 'none',
          visibility: 'hidden',
        }}
      />

      {/* Header */}
      <Group
        justify="space-between"
        px={28}
        py="lg"
        style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', flexShrink: 0 }}
      >
        <Group gap="sm">
          <ThemeIcon size={32} radius="sm" color="red" variant="light">
            <Printer size={16} />
          </ThemeIcon>
          <Text size="xl" fw={700}>
            {title}
          </Text>
        </Group>
        <ActionIcon variant="subtle" color="gray" onClick={onClose} size="lg" radius="sm">
          <X size={18} />
        </ActionIcon>
      </Group>

      {/* Body — preview on the left, controls sidebar on the right */}
      <Group
        gap={0}
        align="stretch"
        wrap="nowrap"
        style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}
      >
      <Stack gap={0} style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        {/* Sub-header: label + zoom */}
        <Group
          justify="space-between"
          px="lg"
          py="xs"
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-2)',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.5px' }}>
            Live Preview
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" c="dimmed">
              Zoom
            </Text>
            <Slider
              min={15}
              max={150}
              value={zoom}
              onChange={setZoom}
              w={120}
              size="xs"
              color="red"
            />
            <Text
              size="xs"
              c="dimmed"
              style={{ fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}
            >
              {zoom}%
            </Text>
          </Group>
        </Group>

        {/* Pannable preview viewport — native scroll + drag-to-pan */}
        <Box
          ref={panViewportRef}
          onMouseDown={handlePanStart}
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
            background: 'var(--mantine-color-gray-1)',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            paddingTop: 16,
            paddingBottom: 16,
          }}
        >
          {pageStartOffsetsMm.map((offsetMm, i) => (
            <Box
              key={i}
              style={{
                width: `calc(${paperW} * ${zoom / 100})`,
                height: `calc(${paperH} * ${zoom / 100})`,
                margin: '16px auto',
                position: 'relative',
                transition: 'width 200ms ease, height 200ms ease',
              }}
            >
              {/* Outer paper sheet — paperW × paperH, with PAGE_MARGIN_MM padding
                  visible as the white "margin" around the printable area. */}
              <Box
                style={{
                  transformOrigin: 'top left',
                  transform: `scale(${zoom / 100})`,
                  transition: 'transform 200ms ease',
                  width: paperW,
                  height: paperH,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)',
                  background: '#fff',
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  padding: `${PAGE_MARGIN_MM}mm`,
                  boxSizing: 'border-box',
                }}
              >
                {/* Inner clipped slot — printable area */}
                <Box
                  style={{
                    width: innerW,
                    height: innerH,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <iframe
                    ref={(el) => {
                      pageIframeRefs.current[i] = el;
                      // Write immediately on assign so the iframe never relies
                      // on a delayed effect to populate its first paint.
                      if (el) writePageFrame(el);
                    }}
                    title={`Print Preview Page ${i + 1}`}
                    style={{
                      position: 'absolute',
                      top: `-${offsetMm}mm`,
                      left: 0,
                      width: innerW,
                      height: `${totalHeightMm}mm`,
                      border: 'none',
                      display: 'block',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              </Box>
              {/* Page number indicator (does not scale with zoom) */}
              <Text
                size="xs"
                c="dimmed"
                style={{
                  position: 'absolute',
                  bottom: -18,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                }}
              >
                Page {i + 1} of {pageCount}
              </Text>
            </Box>
          ))}
        </Box>

      </Stack>

        {/* Right sidebar — orientation + actions */}
        <Stack
          gap="md"
          p="lg"
          style={{
            width: 260,
            flexShrink: 0,
            borderLeft: '1px solid var(--mantine-color-gray-2)',
            background: 'var(--mantine-color-gray-0)',
            justifyContent: 'space-between',
          }}
        >
          <Stack gap="md">
            <Stack gap="xs">
              <Text
                size="xs"
                fw={600}
                tt="uppercase"
                c="dimmed"
                style={{ letterSpacing: '0.6px' }}
              >
                Page Info
              </Text>
              <Badge variant="light" color="gray" size="sm" style={{ alignSelf: 'flex-start' }}>
                {orientationLabel} · A4 · {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                {fitScale < 1 ? ` · fit ${Math.round(fitScale * 100)}%` : ''}
              </Badge>
            </Stack>

            {allowOrientationToggle && (
              <Stack gap="xs">
                <Text
                  size="xs"
                  fw={600}
                  tt="uppercase"
                  c="dimmed"
                  style={{ letterSpacing: '0.6px' }}
                >
                  Orientation
                </Text>
                <SegmentedControl
                  size="xs"
                  fullWidth
                  value={orientation}
                  onChange={(v) => setOrientation(v as 'portrait' | 'landscape')}
                  data={[
                    { value: 'portrait', label: 'Portrait' },
                    { value: 'landscape', label: 'Landscape' },
                  ]}
                />
              </Stack>
            )}
          </Stack>

          <Stack gap="sm">
            <Button
              color="red"
              fullWidth
              leftSection={<Printer size={14} />}
              onClick={handleSendToPrint}
            >
              Send to Print
            </Button>
            <Button variant="default" fullWidth onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Group>
    </Modal>
  );
}
